import {
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';

import { userRoleSchema, type UserRole } from '@/core/schemas';
import { toAppError } from '@/infrastructure/errors/AppError';

import { firebaseAuth } from './app';
import { isDevSignInEnabled, signInForDevelopment } from './devAuth';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export async function signInWithGoogle(): Promise<void> {
  try {
    // Local development against the emulators skips the Google popup entirely:
    // one click signs the developer in. This branch is unreachable in a
    // production build, where `useEmulators` is baked to false.
    if (isDevSignInEnabled()) {
      await signInForDevelopment();
      return;
    }

    await signInWithPopup(firebaseAuth, googleProvider);
  } catch (error) {
    throw toAppError(error);
  }
}

/** Whether the current build signs in without the Google popup (dev only). */
export { isDevSignInEnabled };

export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(firebaseAuth);
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Reads the role from the ID token, never from Firestore.
 *
 * Anything other than a valid `admin` claim means captain — an unknown or
 * tampered value must never grant more than the default.
 */
export async function resolveRole(user: User): Promise<UserRole> {
  const token = await user.getIdTokenResult();
  const parsed = userRoleSchema.safeParse(token.claims.role);
  return parsed.success ? parsed.data : 'captain';
}

/**
 * Fires on sign-in, sign-out **and** token refresh. Listening to the token
 * rather than only the user means a freshly granted admin claim takes effect
 * on the next refresh instead of requiring a reload.
 */
export function observeIdToken(callback: (user: User | null) => void): () => void {
  return onIdTokenChanged(firebaseAuth, callback);
}
